import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogIn, Rocket } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const links = [
  { label: "Home", href: "#home" },
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how" },
  { label: "Plan", href: "#buy" },
  { label: "FAQ", href: "#faq" },
];

const CHECKOUT_LINK = "https://plancourr.mojo.page/arrnode-prep";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPortal = location.pathname.startsWith("/portal");

  const handleNav = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 dark:bg-zinc-950/75 border-b border-slate-200/70 dark:border-zinc-800/60"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
          <div className="h-9 w-9 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 flex items-center justify-center overflow-hidden">
            <img
              src="/arrnode-logo.png"
              alt="Arrnode Prep"
              className="h-7 w-7 object-contain dark:invert transition-transform group-hover:scale-110"
              data-testid="brand-logo-img"
            />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight text-slate-900 dark:text-white">
            Arrnode <span className="text-indigo-600 dark:text-indigo-400">Prep</span>
          </span>
        </Link>

        {!isPortal && (
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => handleNav(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3.5 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white rounded-full hover:bg-slate-100/80 dark:hover:bg-zinc-800/60 transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <>
              {!isPortal && (
                <Button
                  data-testid="navbar-portal-btn"
                  onClick={() => navigate("/portal")}
                  className="hidden sm:inline-flex rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white text-sm px-5"
                >
                  Open Portal
                </Button>
              )}
              <Button
                data-testid="navbar-logout-btn"
                variant="ghost"
                onClick={async () => { await logout(); navigate("/"); }}
                className="rounded-full text-sm"
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <button
                onClick={() => window.open(CHECKOUT_LINK, "_blank", "noopener,noreferrer")}
                data-testid="navbar-get-dashboard-btn"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 hover:from-violet-700 hover:via-fuchsia-700 hover:to-rose-600 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 h-9 shadow-md shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 hover:scale-[1.03] transition-all duration-200"
              >
                <Rocket className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Get My Dashboard</span>
                <span className="md:hidden">Get Dashboard</span>
              </button>
              <Button
                data-testid="navbar-login-btn"
                onClick={() => navigate("/login")}
                variant="ghost"
                className="rounded-full text-xs sm:text-sm font-medium text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 h-9 px-3"
              >
                <LogIn className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Login</span>
              </Button>
            </>
          )}
          {!isPortal && (
            <button
              className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-zinc-800"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {open && !isPortal && (
        <div className="md:hidden border-t border-slate-200/70 dark:border-zinc-800/60 bg-white dark:bg-zinc-950">
          <div className="px-5 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => handleNav(l.href)}
                className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-lg"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
